import React, {
  Suspense,
  lazy,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import "./BusinessRulesPage.css";

const ReactMarkdown = lazy(() => import("react-markdown"));

const PRODUCTION_RULES_URL = "/docs/business-rules-sales-ai.md";
const LOCAL_RULES_URL = "http://localhost:8084/business-rules-sales-ai.md";

const getDefaultRulesUrl = () => {
  const isLocalhost = ["localhost", "127.0.0.1"].includes(window.location.hostname);
  return isLocalhost ? LOCAL_RULES_URL : PRODUCTION_RULES_URL;
};

const BusinessRulesPage = () => {
  const { t } = useTranslation();
  const rulesUrl = useMemo(
    () => process.env.REACT_APP_BUSINESS_RULES_URL || getDefaultRulesUrl(),
    [],
  );
  const [content, setContent] = useState("");
  const [status, setStatus] = useState("loading");

  const loadRules = useCallback(async () => {
    setStatus("loading");

    try {
      const response = await fetch(rulesUrl, { cache: "no-store" });
      if (!response.ok) {
        throw new Error(`Business rules unavailable: HTTP ${response.status}`);
      }
      const markdown = await response.text();
      if (!markdown.trim() || /^\s*<!doctype html|^\s*<html\b/i.test(markdown)) {
        throw new Error("Business rules document is empty");
      }
      setContent(markdown);
      setStatus("ready");
    } catch (_error) {
      setContent("");
      setStatus("unavailable");
    }
  }, [rulesUrl]);

  useEffect(() => {
    loadRules();
  }, [loadRules]);

  if (status !== "ready") {
    return (
      <div className="business-rules-page business-rules-status">
        <i
          className={`fa ${status === "loading" ? "fa-spinner fa-spin" : "fa-scale-balanced"}`}
          aria-hidden="true"
        />
        <h2>{t("sidebar.businessRules")}</h2>
        <p>
          {t(status === "loading" ? "businessRules.loading" : "businessRules.unavailable")}
        </p>
        {status === "unavailable" && (
          <button type="button" className="btn btn-primary" onClick={loadRules}>
            <i className="fa fa-refresh" aria-hidden="true" />{" "}
            {t("businessRules.retry")}
          </button>
        )}
      </div>
    );
  }

  return (
    <article className="business-rules-page business-rules-markdown">
      <Suspense fallback={<p>{t("businessRules.loading")}</p>}>
        <ReactMarkdown>{content}</ReactMarkdown>
      </Suspense>
    </article>
  );
};

export default BusinessRulesPage;
